import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { PropertyImage } from "../../models/propertyImage.model";

interface PropertyGalleryProps {
  images?: (string | PropertyImage)[]; // Accept array of URLs or PropertyImage objects
  height?: number; // optional custom height
  onMainImageClick?: () => void; // Optional click handler for main image
}

const normalize = (images?: (string | PropertyImage)[]) => {
  if (!images || images.length === 0) return [];
  return images.map(img => typeof img === 'string' ? img : img.imageUrl).filter(Boolean);
};

const PropertyGallery: React.FC<PropertyGalleryProps> = ({ images, height = 300, onMainImageClick }) => {
  const normalized = normalize(images);
  const mainInitial = normalized.find((_, idx) => {
    if (!images) return idx === 0; // first
    const obj = images && images[idx] as any;
    return obj && typeof obj === 'object' && 'isMainImage' in obj && obj.isMainImage;
  }) || normalized[0];
  const [mainImage, setMainImage] = useState<string | undefined>(mainInitial);

  useEffect(() => {
    // Update main image when images prop changes
    const n = normalize(images);
    const main = n.find((_, idx) => {
      if (!images) return idx === 0;
      const obj = images && images[idx] as any;
      return obj && typeof obj === 'object' && obj.isMainImage;
    }) || n[0];
    setMainImage(main);
  }, [images]);

  if (!mainImage) return null;

  return (
    <div className="row gx-3 w-100 mx-0">
      {/* Main Image */}
      <div 
        className="col-12 col-sm-8 mb-3 mb-sm-0" 
        style={{ position: 'relative' }}
        onMouseEnter={(e) => {
          const eyeIcon = e.currentTarget.querySelector('.eye-icon-overlay') as HTMLElement;
          if (eyeIcon) {
            eyeIcon.style.opacity = '0.9';
            eyeIcon.style.visibility = 'visible';
          }
        }}
        onMouseLeave={(e) => {
          const eyeIcon = e.currentTarget.querySelector('.eye-icon-overlay') as HTMLElement;
          if (eyeIcon) {
            eyeIcon.style.opacity = '0';
            eyeIcon.style.visibility = 'hidden';
          }
        }}
      >
        <img
          src={mainImage}
          className="img-fluid rounded-3 w-100"
          style={{ 
            height: "100%", 
            maxHeight: height, 
            objectFit: "cover",
            cursor: onMainImageClick ? 'pointer' : 'default'
          }}
          alt="Main Property"
          onClick={onMainImageClick}
          title={onMainImageClick ? "Click to view gallery" : undefined}
        />
        {/* Eye icon overlay - only visible on hover */}
        {onMainImageClick && (
          <div 
            className="eye-icon-overlay"
            onClick={onMainImageClick}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              opacity: '0',
              visibility: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
            }}
            title="View Gallery"
          >
            <i className="pi pi-eye" style={{ fontSize: '24px' }}></i>
          </div>
        )}
        {/* Gallery count indicator */}
        {onMainImageClick && (
          <div 
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '6px 10px',
              borderRadius: '15px',
              fontSize: '11px',
              fontWeight: 'bold',
              pointerEvents: 'none'
            }}
          >
            <i className="pi pi-images me-1" style={{ fontSize: '10px' }}></i>
            {normalized.length}
          </div>
        )}
      </div>

      {/* Vertical Swiper Thumbnails */}
      <div className="col-12 col-sm-4 position-relative">
        <Swiper
          modules={[Navigation]}
          direction="vertical"
          spaceBetween={10}
          slidesPerView={3}
          navigation={{
            nextEl: ".swiper-button-next-vertical",
            prevEl: ".swiper-button-prev-vertical"
          }}
          style={{ height }}
        >
          {normalized.map((img, idx) => (
            <SwiperSlide key={idx}>
              <img
                src={img}
                className={`img-fluid rounded-2 w-100 ${img === mainImage ? "border border-2 border-dark" : ""}`}
                style={{
                  height: Math.max(80, Math.floor((height - 30) / 3)), // three thumbnails, min height
                  objectFit: "cover",
                  cursor: "pointer"
                }}
                onClick={() => setMainImage(img)}
                alt={`Thumbnail ${idx}`}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Arrows - custom round buttons to match design */}
        <div className="swiper-button-prev-vertical position-absolute top-0 start-50 translate-middle-x" style={{ transform: 'translate(-50%, -50%)' }}>
          <button className="thumb-nav-btn" aria-label="previous thumbnail" title="Previous">
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 0L18 12H0L9 0Z" fill="white"/>
            </svg>
          </button>
        </div>
        <div className="swiper-button-next-vertical position-absolute bottom-0 start-50 translate-middle-x" style={{ transform: 'translate(-50%, 50%)' }}>
          <button className="thumb-nav-btn" aria-label="next thumbnail" title="Next">
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(180deg)' }}>
              <path d="M9 0L18 12H0L9 0Z" fill="white"/>
            </svg>
          </button>
        </div>

        <style>
          {`
            .thumb-nav-btn {
              width: 46px;
              height: 46px;
              border-radius: 50%;
              background: #000;
              border: none;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 6px 16px rgba(0,0,0,0.18);
              cursor: pointer;
              padding: 6px;
            }
            .thumb-nav-btn svg { display: block; }
            .thumb-nav-btn:active { transform: scale(0.98); }
            .swiper-button-prev-vertical, .swiper-button-next-vertical { z-index: 10; }
          `}
        </style>
      </div>
    </div>
  );
};

export default PropertyGallery;
